import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { capitalize, formatCurrency } from "@sellar-io/common";
import * as ROUTES from "../../../../constants/routes";
import { useCurrentDate, Icon, H5, InputText } from "@sellar-io/components";
import type {
  PayoutListResponseType,
  InvoicePayoutType,
  CreditNotePayoutType,
  NextPayoutResponseType,
  InvoiceNextPayoutType,
} from "@sellar-io/api-v2/types";
import { SELLAR_BUSINESS_ID } from "../../../../constants/sellarConstants.shared";
import { InputContainer } from "@sellar-io/components";

import { SellarCreditDrawer } from "./SellarCreditDrawer";
import { FilterPopover } from "~/components/filterPopover/FilterPopover";
import { PayoutFilter } from "./PayoutFilter";
import {
  usePayoutStore,
  PayoutStoreState,
  type DocumentType,
} from "../stores/PayoutFilterStore";
import { useShallow } from "zustand/react/shallow";
import { Tag } from "~/components/tag/Tag";
import { Dot } from "./SellarCreditStats";
import { useSearchParams } from "react-router";
import { TanStackTableSimple } from "@sellar-io/components/adapters";
import {
  columnHelper,
  getEmptyMessage,
  getBadgeColor,
} from "./PayoutTable.shared";

type BaseTableRow = {
  id: number | null;
  customId: string;
  date: string | Date;
  customer: string;
  total: number;
};

type TableRow =
  | (BaseTableRow & {
      type: "invoice" | "fee";
      order: { id: number };
    })
  | (BaseTableRow & {
      type: "credit-note";
      order?: undefined;
    });

export type SellarCreditDocument = {
  type: DocumentType;
  contents: InvoicePayoutType | CreditNotePayoutType | InvoiceNextPayoutType;
};

function PayoutTable({
  payout,
  setActive,
}: {
  payout: PayoutListResponseType[number] | NextPayoutResponseType;
  setActive: (value: boolean) => void;
}) {
  const currentDate = useCurrentDate();
  const [search, setSearch] = useState("");
  const [desktopFilter, setDesktopFilter] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const invoiceId = Number(searchParams.get("invoiceId"));
  const selectedInv = payout.Invoices.find((i) => i.id === invoiceId);

  // State for viewing a selected document
  const [activeDocument, setActiveDocument] = useState(!!selectedInv);
  const [selectedDocument, setSelectedDocument] = useState<
    SellarCreditDocument | undefined
  >(selectedInv ? { type: "invoice", contents: selectedInv } : undefined);
  const [previousSelectedInvoice, setPreviousSelectedInvoice] =
    useState(selectedInv);
  if (selectedInv !== previousSelectedInvoice) {
    setPreviousSelectedInvoice(selectedInv);
    setSelectedDocument(
      selectedInv ? { type: "invoice", contents: selectedInv } : undefined,
    );
  }
  // Use our document-type store.
  const { documentType, setDocumentType } = usePayoutStore(
    useShallow((state: PayoutStoreState) => state),
  );

  const { feeInvoice, invoicesTotal, creditNotesTotal } = useMemo(() => {
    const feeInvoice = payout.Invoices.find(
      (invoice) =>
        invoice.type === "sellarCreditSupplier" &&
        invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
    ) || { id: "", total: 0, RecipientBusiness: { name: "" } };

    const invoicesTotal =
      payout.Invoices.reduce((acc, invoice) => {
        if (invoice.SenderBusinessId !== SELLAR_BUSINESS_ID) {
          return acc + (invoice.total || 0);
        }
        return acc;
      }, 0) || 0;

    const creditNotesTotal =
      payout.CreditNotes.reduce(
        (acc, creditNote) => acc + (creditNote.total || 0),
        0,
      ) || 0;

    return { feeInvoice, invoicesTotal, creditNotesTotal };
  }, [payout]);

  // Convert payout into TableRow[] for Tanstack Table
  const data = useMemo<TableRow[]>(() => {
    if (!payout) return [];

    const feeInvoiceLocal =
      payout.Invoices.find(
        (invoice) =>
          invoice.type === "sellarCreditSupplier" &&
          invoice.SenderBusinessId === SELLAR_BUSINESS_ID,
      ) || null;

    const invoiceRows = payout.Invoices.filter(
      (invoice) => !feeInvoiceLocal || invoice.id !== feeInvoiceLocal.id,
    ).map<TableRow>((invoice) => ({
      id: invoice.id,
      customId: invoice.customId || "",
      type: "invoice",
      order: { id: invoice.OrderId || 0 },
      date: invoice.issuedAt || currentDate,
      customer: invoice.Order?.Retailer?.Business?.name || "",
      total: invoice.total,
    }));

    const feeRow = feeInvoiceLocal
      ? {
          id: feeInvoiceLocal.id,
          customId: feeInvoiceLocal.customId || "",
          type: "fee" as const,
          order: { id: feeInvoiceLocal.OrderId || 0 },
          date: feeInvoiceLocal.issuedAt || currentDate,
          customer: "",
          total: feeInvoiceLocal.total,
        }
      : null;

    const creditNoteRows = (payout.CreditNotes || []).map<TableRow>(
      (creditNote) => ({
        id: creditNote.id,
        customId: creditNote.customId || "",
        type: "credit-note" as const,
        date: creditNote.issuedAt || currentDate,
        customer: creditNote.Invoice?.Order?.Retailer?.Business?.name || "",
        total: creditNote.total,
      }),
    );

    return [...invoiceRows, ...creditNoteRows, ...(feeRow ? [feeRow] : [])];
  }, [payout, currentDate]);

  // Filter rows by search and by document type from the store.
  const filteredData = useMemo(() => {
    let tempData = data;
    if (documentType && documentType !== "all") {
      tempData = tempData.filter((row) => row.type === documentType);
    }
    if (search) {
      tempData = tempData.filter(
        (row) =>
          row.customer.toLowerCase().includes(search.toLowerCase()) ||
          row.id?.toString().toLowerCase().includes(search.toLowerCase()) ||
          row.customId.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return tempData;
  }, [data, search, documentType]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("type", {
        header: "Type",
        cell: (info) => {
          switch (info.getValue()) {
            case "invoice":
              return (
                <div className="no-wrap flex">
                  <Icon icon="FILE_DOCUMENT_OUTLINE" />
                  <span className="text-sm">Inv</span>
                </div>
              );
            case "credit-note":
              return (
                <div className="no-wrap flex">
                  <Icon icon="FILE_DOCUMENT_OUTLINE" />
                  <span className="text-sm">CrN</span>
                </div>
              );
            case "fee":
              return (
                <div className="no-wrap ml-0.5 flex items-center">
                  <div className="flex h-4 w-4 items-center justify-center rounded-full bg-linear-to-br from-[#B557F1] to-[#D213A6]">
                    <Icon icon="SELLAR_NEW" size={0.5} className="text-white" />
                  </div>
                  <span className="text-sm">&nbsp;Fee</span>
                </div>
              );
            default:
              return "";
          }
        },
      }),
      columnHelper.accessor("customId", {
        header: "ID",
        cell: (info) => {
          return info.getValue() || "";
        },
      }),
      columnHelper.accessor("order", {
        header: "Order ID",
        cell: (info) => info.getValue()?.id || "",
      }),
      columnHelper.accessor("date", {
        header: "Date",
        cell: (info) => {
          return info.row.original.type === "fee" &&
            payout.status === "optimistic"
            ? ""
            : dayjs(info.getValue()).format("DD/MM/YYYY");
        },
      }),
      columnHelper.accessor("customer", {
        header: "Customer",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("total", {
        header: "Total",
        cell: (info) => {
          const sign = info.row.original.type === "invoice" ? "" : "-";
          return `${sign}${formatCurrency(info.getValue())}`;
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "action",
        header: "",
        enableSorting: false,
        cell: () => (
          <div className="flex w-full justify-end">
            <Icon icon="CHEVRON_RIGHT" size={1} />
          </div>
        ),
      }),
    ],
    [payout.status],
  );

  // Handler for row clicks: open the drawer with full document details.
  function handleRowClick(rowData: TableRow) {
    if (!payout || (payout.status === "optimistic" && rowData.type === "fee"))
      return;
    if (rowData.type === "credit-note") {
      const creditNote = payout.CreditNotes.find((cn) => cn.id === rowData.id);
      if (creditNote) {
        setSelectedDocument({ type: "credit-note", contents: creditNote });
      }
    } else {
      const invoice = payout.Invoices.find((inv) => inv.id === rowData.id);
      if (invoice) {
        setSelectedDocument({ type: rowData.type, contents: invoice });
      }
    }
    setActiveDocument(true);
  }

  return (
    <>
      <H5 className="my-[26px] flex flex-wrap items-center space-x-1 bg-transparent text-base font-medium">
        <div
          className="cursor-pointer hover:underline"
          onClick={() => {
            const params = new URLSearchParams(searchParams);
            if (params.get("invoiceId")) {
              navigate(ROUTES.BUSINESS_FINANCE_CREDIT);
            } else {
              params.delete("payout");
              setSearchParams(params);
              setActive(false);
            }
          }}
        >
          Sellar Credit
        </div>
        <Icon icon="CHEVRON_RIGHT" className="m-0" />
        {payout.status === "optimistic"
          ? "Current balance"
          : `Payout ${payout.id || ""}`}{" "}
        <div className="flex flex-col px-2">
          {payout.payoutDate && getBadgeColor(payout.status, payout.payoutDate)}
        </div>
      </H5>
      <div className="inline-flex rounded-lg border border-zinc-300 bg-white py-4">
        <div className="flex flex-col gap-2 border-r px-6">
          <span className="text-xs font-semibold">Total order amount</span>
          <span className="text-xl">
            {formatCurrency(invoicesTotal - creditNotesTotal)}
          </span>
        </div>
        <div className="flex flex-col gap-2 px-6">
          <span className="text-xs font-semibold">Estimated payout</span>
          <span className="flex items-center gap-1 text-xl text-zinc-800">
            <Dot color="bg-emerald-800" />
            {formatCurrency(
              invoicesTotal - (creditNotesTotal + feeInvoice.total),
            )}
          </span>
        </div>
      </div>

      <div className="container mx-auto mt-4 max-w-[1300px]">
        {/* Table header with search */}
        <div className="flex items-center rounded-t-lg border border-b-0 border-zinc-300 bg-zinc-50 p-4">
          <div className="flex flex-col gap-2">
            <div className="flex">
              <InputContainer className="mr-2 max-w-[500px] min-w-[300px] py-[0.4rem]">
                <InputText
                  required={false}
                  placeholder="Search customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full"
                />
              </InputContainer>
              <FilterPopover
                icon="FILTER_VARIANT"
                buttonText="Filter"
                compact={false}
                active={desktopFilter}
                toggleActive={(value: boolean) => setDesktopFilter(value)}
              >
                <PayoutFilter onDismiss={() => setDesktopFilter(false)} />
              </FilterPopover>
            </div>
            {documentType && documentType !== "all" && (
              <div className="flex">
                <Tag
                  className="flex items-center gap-1"
                  onClick={() => setDocumentType("all")}
                >
                  {capitalize(documentType)}
                </Tag>
              </div>
            )}
          </div>
        </div>

        <TanStackTableSimple
          columns={columns}
          data={filteredData}
          onRowClick={(row) => {
            handleRowClick(row);
          }}
          noResultsText={getEmptyMessage(search, documentType)}
          paginationLabel="Results per page"
          gridClasses="grid-cols-[repeat(6,_1fr)_40px]"
        />

        <SellarCreditDrawer
          active={activeDocument}
          setActive={() => setActiveDocument(false)}
          document={selectedDocument}
        />
      </div>
    </>
  );
}

export default PayoutTable;
